// A live subscription to one node of Meal Hat's database — over the same
// REST door as everything else in store/db.js, streamed.
//
// Cook mode is followed on two devices at once (Matt, 2026-10-02: "switch
// back and forth between my iPad and my phone and it'll resume where I
// was"), so polling would be slow and chatty. The Realtime Database SDK
// does this well but costs 216 KB in the bundle (measured); Firebase's REST
// API streams the same changes to a plain EventSource, which the browser
// has built in. Events: `put` (the whole value at `path`), `patch`,
// `keep-alive`, `cancel`, `auth_revoked`. The nodes followed here are only
// ever written whole, so a root `put` IS the value; anything else just
// re-reads the node, which is rare and cheap.
//
// The auth token in the URL expires after an hour, and EventSource
// reconnects to the SAME url, so on any error the stream is rebuilt with a
// fresh token (after a short pause, so a dead network does not spin).
import { authToken, buildUrl, dbGet } from '../store/db.js';

const RETRY_MS = 3000;

/** Calls `onData(value)` now and on every change; returns the unsubscribe. */
export function subscribe (path, onData, onError = () => {}) {
  let source = null;
  let closed = false;
  let retry = null;

  async function connect () {
    if (closed) return;
    const token = await authToken();
    if (closed) return;
    source = new EventSource(buildUrl(path, token));
    source.addEventListener('put', (event) => {
      try {
        const { path: at, data } = JSON.parse(event.data);
        if (at === '/') onData(data);
        else dbGet(path).then(onData).catch(onError);
      } catch (error) { onError(error); }
    });
    source.addEventListener('patch', () => { dbGet(path).then(onData).catch(onError); });
    source.addEventListener('auth_revoked', () => reconnect());
    source.addEventListener('cancel', () => reconnect());
    source.onerror = () => reconnect();
  }
  function reconnect () {
    source?.close();
    source = null;
    if (closed) return;
    clearTimeout(retry);
    retry = setTimeout(connect, RETRY_MS);
  }
  connect();

  return () => {
    closed = true;
    clearTimeout(retry);
    source?.close();
  };
}
